<script lang="ts">
  import { page } from "$app/stores";
  import type { LayoutData } from "./$types";
  import { Quotes } from "phosphor-svelte";
  import "$lib/styles/community_view.css";
  import { activeSection, activeColor } from "$lib/data";
  import { get_pattern } from "$lib/utilities";
  import { onMount } from "svelte";
  import { dev } from "$app/environment";
  import ProfileHeader from "./ProfileHeader.svelte";
  import toast from "svelte-french-toast";
  import { invalidateAll } from "$app/navigation";
  onMount(() => {
    $activeSection = "home";
    $activeColor = 5;
  });

  export let data: LayoutData;

  $: if (dev) console.log(data);

  const base_url = `/community/${data.member.handle}`;

  let cc = data.contribution_count;
  if (data.bonus_contributions.length > 0) {
    cc += ` / ${data.bonus_contributions.length}`;
  }

  const menu = [
    {
      title: "Contributions",
      link: "",
      count: cc,
    },
    {
      title: "Process Videos",
      link: "/process-videos",
      count: data.process_video_count,
    },
    {
      title: "Curated Galleries",
      link: "/galleries",
      count: data.gallery_count,
    },
  ];

  const clear_cache = async () => {
    const res = await fetch("/api/community/clear-cache", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ handle: data.member.handle }),
    });
    console.log(res);
    if (res.ok) {
      toast.success("Cache Cleared", {
        duration: 5000,
        position: "bottom-center",
      });
      await invalidateAll();
    } else {
      toast.error("Error clearing cache", {
        duration: 5000,
        position: "bottom-center",
      });
    }
  };
</script>

{#if data.is_me && data.user?.profile?.display_name}
  <div class="col-full">
    <div class="is_me">
      <div class="content">
        <div class="first">
          {#if data.user.support_level >= 5}
            ADMIN
          {:else}
            Your Profile
          {/if}
        </div>
        <div class="nav">
          <button on:click={clear_cache}> Clear Cache </button>
          <a href="/members/profile">Edit your Profile</a>
        </div>
      </div>
    </div>
  </div>
{/if}

<div class="col-full" class:is_me={data.is_me}>
  <ProfileHeader member={data.member} />
</div>
<div class="col">
  <aside>
    <div class="header" style:background-image={get_pattern()}>
      Showcase Menu
    </div>
    <nav>
      {#each menu as item}
        {@const href = base_url + item.link}
        {#if item.count > 0 || item.link === ""}
          <a
            {href}
            data-sveltekit-noscroll
            class:active={href === $page.url.pathname}
          >
            {item.title}
            <div class="count">
              {item.count}
            </div>
          </a>
        {/if}
      {/each}
    </nav>
    {#if data.member.profile?.biography}
      <div class="biography">
        <div class="quote_start">
          <Quotes size="1.6rem" weight="fill" color="var(--dots)" />
        </div>
        <div class="quote_end">
          <Quotes size="1.6rem" weight="fill" color="var(--dots)" />
        </div>
        <div class="text">
          {data.member.profile.biography}
        </div>
      </div>
    {/if}
  </aside>
  <div class="content">
    <slot />
  </div>
</div>

<style>
  .count {
    color: var(--offDark);
  }
  .biography {
    padding: 2rem;
    font-size: 0.9rem;
    position: relative;
    line-height: 1.7;
    color: var(--offDark);
  }
  .biography .text {
    position: relative;
    z-index: 1;
    text-align: justify;
    padding: 0.5rem 0;
  }
  .quote_start {
    position: absolute;
    top: 0.7rem;
    left: 0.5rem;
    z-index: 0;
  }
  .quote_end {
    position: absolute;
    bottom: 0.7rem;
    right: 0.5rem;
    z-index: 0;
    transform: rotate(180deg);
  }
  .col {
    display: grid;
    grid-template-columns: 1fr 3fr;
    gap: 2rem;
    max-width: 1200px;
    padding: 0;
    margin: 0 auto 2rem auto;
    position: relative;
    z-index: 1;
  }

  @media (max-width: 1324px) {
    .col,
    .col-full {
      padding: 0 4rem;
      max-width: 100%;
    }
  }
  @media (max-width: 1024px) {
    .col,
    .col-full {
      padding: 0 1rem;
    }
  }

  .is_me {
    max-width: 1200px;
    padding: 2rem 0 0 0;
    margin: 0 auto;
    position: relative;
    z-index: 1;
  }
  .is_me .content {
    background: white;
    box-shadow: var(--shadow);
    border-radius: 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .is_me .content .first {
    padding: 1rem;
    font-weight: bold;
    color: var(--offDark);
    border-right: 1px solid var(--dots);
    font-size: 0.9rem;
  }
  .is_me .content .nav {
    display: flex;
    align-items: center;
  }

  .is_me .content .nav button {
    padding: 1rem;
    background: transparent;
    border: none;
    cursor: pointer;
    font-size: 0.8rem;
    color: var(--title);
    font-weight: bold;
    border-left: 1px solid var(--dots);
  }

  .is_me .content .nav a {
    padding: 1rem;
    text-decoration: none;
    font-size: 0.8rem;
    color: var(--title);
    font-weight: bold;
    border-left: 1px solid var(--dots);
  }
  .col-full {
    max-width: 1200px;
    padding: 2rem 0 0 0;
    margin: 0 auto 2rem auto;
    position: relative;
    z-index: 1;
  }

  @media (max-width: 1324px) {
    .col,
    .col-full {
      padding: 0 4rem;
      max-width: 100%;
    }
  }
  @media (max-width: 1024px) {
    .col,
    .col-full {
      padding: 0 1rem;
    }
  }

  @media (max-width: 768px) {
    .col,
    .col-full {
      padding: 0;
      margin: 0;
    }

    .col-full .is_me {
      margin: 0 1rem;
    }
    .col-full .is_me .nav {
      display: flex;
      justify-content: space-between;
      width: 100%;
    }

    .is_me .first {
      display: none;
    }
  }
  aside {
    background-color: white;
    box-shadow: var(--shadow);
    border-radius: 1rem;
    overflow: hidden;
  }
  aside .header {
    padding: 1rem 1rem 0.8rem 1rem;
    border-bottom: 1px solid var(--dots);
    color: var(--title);
    font-weight: bold;
    border-top: 1px solid var(--dots);
  }

  aside .header:nth-of-type(1) {
    border-top: none;
  }

  aside nav {
    display: flex;
    flex-direction: column;
  }
  aside nav a {
    display: flex;
    padding: 1rem;
    border-bottom: 1px solid var(--dots);
    font-size: 0.8rem;
    text-decoration: none;
    justify-content: space-between;
    align-items: center;
  }

  aside nav a:hover {
    background-color: var(--offWhite);
    color: var(--secondary);
  }
  aside nav a.active,
  aside nav a.active .count {
    color: var(--secondary);
  }

  @media (max-width: 768px) {
    .col {
      padding: 1rem;
      grid-template-columns: 1fr;
    }

    aside {
      margin-bottom: 2rem;
    }

    aside nav {
      margin-bottom: 1rem;
    }

    aside nav:last-of-type {
      margin-bottom: 0;
    }
  }
  @media (max-width: 600px) {
    .col {
      padding: 0;
      grid-template-columns: 1fr;
    }
    aside {
      border-radius: 0;
      margin: 0;
    }
  }
</style>
